/**
 * Landing - Professional Minimalist Landing Page (Tailwind Version)
 * Design: 2-color palette with monochromatic line icons
 * Primary: #2563eb (Professional Blue)
 * Accent: #0ea5e9 (Light Blue)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../useAuth';
import SEOHelmet from '../components/SEOHelmet';

const Landing: React.FC = () => {
  const { user } = useAuth();

  return (
    <>
      <SEOHelmet
        title="Home - Professional Continuing Education Credits Tracker"
        description="Track your professional continuing education credits effortlessly. CEC Track helps architects, engineers, pharmacists, landscape architects, and other professionals manage renewal cycles and stay compliant with state requirements."
        keywords="continuing education credits, CEC tracking, professional license renewal, architect CECs, engineer PDH, pharmacist CE, landscape architect credits, license compliance, state requirements"
        canonical="https://cectrack.com/"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CEC Track",
            "alternateName": ["CECTrack", "CEC Tracker"],
            "url": "https://cectrack.com",
            "logo": "https://cectrack.com/logo-512.png",
            "image": "https://cectrack.com/logo-512.png",
            "description": "Professional continuing education credit tracking platform for architects, engineers, landscape architects, pharmacists, CPAs, nurses, social workers, and other licensed professionals. Manage renewal cycles and stay compliant with state requirements across all 50 states.",
            "foundingDate": "2024",
            "slogan": "Never Miss a Licensure Renewal Again",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Support",
              "email": "support@cectrack.com",
              "url": "https://cectrack.com/contact",
              "availableLanguage": "English"
            },
            "sameAs": [
              "https://github.com/cymatia2/cec-tracker"
            ],
            "offers": {
              "@type": "Offer",
              "name": "CEC Track Free Plan",
              "price": "0",
              "priceCurrency": "USD",
              "description": "Free continuing education tracking for professionals"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "CEC Track",
            "url": "https://cectrack.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://cectrack.com/search?q={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CEC Track",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "150"
            }
          }
        ]}
      />

      <div className="tw-font-sans tw-text-slate-900 tw-leading-relaxed">
        {/* Hero Section */}
        <section className="tw-bg-white tw-py-32 tw-border-b tw-border-gray-200">
          <div className="container tw-max-w-6xl tw-mx-auto tw-px-6">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="tw-mb-6">
                  <span className="tw-inline-block tw-px-4 tw-py-1.5 tw-bg-blue-50 tw-text-primary-500 tw-text-sm tw-font-semibold tw-rounded-full tw-tracking-wide">
                    PROFESSIONAL LICENSE MANAGEMENT
                  </span>
                </div>
                <h1 className="tw-text-5xl md:tw-text-6xl tw-font-bold tw-leading-tight tw-mb-6 tw-text-slate-900 tw--tracking-tight">
                  Never Miss a License Renewal Again
                </h1>
                <p className="tw-text-xl tw-leading-relaxed tw-text-slate-600 tw-mb-10 tw-max-w-xl">
                  Track continuing education credits across all states in one centralized platform. 
                  Stay compliant, organized, and ready for renewal.
                </p>
                <div className="tw-flex tw-gap-4 tw-flex-wrap">
                  <Link
                    to={user ? "/dashboard" : "/register"}
                    className="tw-inline-flex tw-items-center tw-px-8 tw-py-4 tw-bg-primary-500 tw-text-white tw-text-base tw-font-semibold tw-rounded-lg tw-no-underline tw-transition-all tw-duration-200 hover:tw-bg-primary-600 hover:tw--translate-y-0.5 hover:tw-shadow-lg"
                  >
                    {user ? "Go to Dashboard" : "Get Started Free"}
                    <svg className="tw-ml-2 tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="tw-inline-flex tw-items-center tw-px-8 tw-py-4 tw-bg-transparent tw-text-primary-500 tw-text-base tw-font-semibold tw-rounded-lg tw-no-underline tw-border-2 tw-border-gray-200 tw-transition-all tw-duration-200 hover:tw-border-primary-500 hover:tw-bg-slate-50"
                  >
                    Learn More
                  </Link>
                </div>
                <div className="tw-mt-10 tw-flex tw-items-center tw-gap-6 tw-flex-wrap">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <svg className="tw-w-5 tw-h-5 tw-text-accent-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span className="tw-text-sm tw-text-slate-600 tw-font-medium">Free forever</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <svg className="tw-w-5 tw-h-5 tw-text-accent-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span className="tw-text-sm tw-text-slate-600 tw-font-medium">No credit card required</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <svg className="tw-w-5 tw-h-5 tw-text-accent-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span className="tw-text-sm tw-text-slate-600 tw-font-medium">Setup in 5 minutes</span>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 d-none d-lg-block">
                <div className="tw-p-12 tw-bg-slate-50 tw-rounded-2xl tw-border tw-border-gray-200">
                  {/* Stats Grid */}
                  <div className="tw-grid tw-grid-cols-2 tw-gap-6">
                    <div className="tw-p-6 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200">
                      <div className="tw-text-3xl tw-font-bold tw-text-primary-500 tw-mb-2">50+</div>
                      <div className="tw-text-sm tw-text-slate-600 tw-font-medium">States Supported</div>
                    </div>
                    <div className="tw-p-6 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200">
                      <div className="tw-text-3xl tw-font-bold tw-text-primary-500 tw-mb-2">30+</div>
                      <div className="tw-text-sm tw-text-slate-600 tw-font-medium">Professions</div>
                    </div>
                    <div className="tw-p-6 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200">
                      <div className="tw-text-3xl tw-font-bold tw-text-primary-500 tw-mb-2">1000+</div>
                      <div className="tw-text-sm tw-text-slate-600 tw-font-medium">Active Users</div>
                    </div>
                    <div className="tw-p-6 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200">
                      <div className="tw-text-3xl tw-font-bold tw-text-primary-500 tw-mb-2">99%</div>
                      <div className="tw-text-sm tw-text-slate-600 tw-font-medium">On-Time Renewals</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="tw-py-32 tw-bg-white">
          <div className="container tw-max-w-6xl tw-mx-auto tw-px-6">
            <div className="tw-text-center tw-mb-20 tw-max-w-3xl tw-mx-auto">
              <h2 className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-4 tw-text-slate-900 tw--tracking-tight">
                Everything You Need to Stay Compliant
              </h2>
              <p className="tw-text-lg tw-text-slate-600 tw-leading-relaxed">
                Professional-grade tools designed for licensed professionals managing multi-state requirements
              </p>
            </div>

            <div className="tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-8">
              {[
                {
                  icon: (
                    <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  ),
                  title: 'Multi-State Tracking',
                  description: 'Manage licenses and CEC requirements across multiple states from a single dashboard. See all renewal cycles at a glance.'
                },
                {
                  icon: (
                    <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  ),
                  title: 'Certificate Storage',
                  description: 'Securely store all course certificates in the cloud. Access them anytime, anywhere, and never lose important documentation.'
                },
                {
                  icon: (
                    <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  ),
                  title: 'Smart Reminders',
                  description: 'Receive timely notifications about upcoming deadlines and missing credits. Stay ahead of requirements effortlessly.'
                },
                {
                  icon: (
                    <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  ),
                  title: 'Compliance Dashboard',
                  description: 'Real-time compliance status for each license. Visual progress tracking shows exactly where you stand for every renewal cycle.'
                },
                {
                  icon: (
                    <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  ),
                  title: 'Secure & Private',
                  description: 'Bank-level encryption protects your data. Your certificates and personal information are always safe and confidential.'
                },
                {
                  icon: (
                    <svg className="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  ),
                  title: 'Lightning Fast',
                  description: 'Upload certificates and get instant credit calculations. No waiting, no manual data entry - just upload and done.'
                }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="tw-p-8 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-transition-all tw-duration-300 hover:tw-border-primary-500 hover:tw-shadow-lg hover:tw--translate-y-1"
                >
                  <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-blue-50 tw-text-primary-500 tw-flex tw-items-center tw-justify-center tw-mb-5">
                    {feature.icon}
                  </div>
                  <h3 className="tw-text-xl tw-font-semibold tw-mb-3 tw-text-slate-900">
                    {feature.title}
                  </h3>
                  <p className="tw-text-base tw-text-slate-600 tw-leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="tw-py-32 tw-bg-slate-50">
          <div className="container tw-max-w-6xl tw-mx-auto tw-px-6">
            <div className="tw-text-center tw-mb-20">
              <h2 className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-4 tw-text-slate-900 tw--tracking-tight">
                Simple Process, Powerful Results
              </h2>
              <p className="tw-text-lg tw-text-slate-600 tw-leading-relaxed">
                Get started in minutes with our streamlined workflow
              </p>
            </div>

            <div className="tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-12">
              {[
                {
                  step: '01',
                  title: 'Add Your Licenses',
                  description: 'Enter your professional licenses and state requirements. We support 30+ professions across all 50 states.'
                },
                {
                  step: '02',
                  title: 'Upload Certificates',
                  description: 'Add completed CE classes with certificates. Drag and drop files or take photos with your phone.'
                },
                {
                  step: '03',
                  title: 'Track Progress',
                  description: 'Monitor compliance status in real-time. See exactly what you need for each renewal cycle.'
                },
                {
                  step: '04',
                  title: 'Stay Compliant',
                  description: 'Receive timely reminders and never miss a deadline. Renew with confidence every time.'
                }
              ].map((item, index) => (
                <div key={index} className="tw-text-center">
                  <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-rounded-full tw-bg-primary-500 tw-text-white tw-text-xl tw-font-bold tw-mb-6">
                    {item.step}
                  </div>
                  <h3 className="tw-text-xl tw-font-semibold tw-mb-3 tw-text-slate-900">
                    {item.title}
                  </h3>
                  <p className="tw-text-base tw-text-slate-600 tw-leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professions Section */}
        <section className="tw-py-32 tw-bg-white">
          <div className="container tw-max-w-6xl tw-mx-auto tw-px-6">
            <div className="tw-text-center tw-mb-16">
              <h2 className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-4 tw-text-slate-900 tw--tracking-tight">
                Built for Licensed Professionals
              </h2>
              <p className="tw-text-lg tw-text-slate-600 tw-leading-relaxed">
                Supporting 30+ professions across all 50 states
              </p>
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-justify-center tw-max-w-4xl tw-mx-auto">
              {[
                'Architects',
                'Professional Engineers',
                'Landscape Architects',
                'CPAs',
                'Pharmacists',
                'Registered Nurses',
                'Social Workers',
                'Real Estate Agents',
                'Attorneys',
                'Dentists',
                'Physical Therapists',
                'Counselors',
                'Appraisers',
                'Land Surveyors',
                'Dental Hygienists',
                'Interior Designers'
              ].map((profession, index) => (
                <div
                  key={index}
                  className="tw-px-6 tw-py-3 tw-bg-slate-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium tw-text-slate-700 tw-transition-all tw-duration-200 hover:tw-bg-blue-50 hover:tw-border-primary-500 hover:tw-text-primary-500"
                >
                  {profession}
                </div>
              ))}
            </div>

            <div className="tw-text-center tw-mt-12">
              <p className="tw-text-base tw-text-slate-600">
                ...and many more licensed professionals requiring continuing education credits
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="tw-py-24 tw-bg-slate-900 tw-text-white">
          <div className="container tw-max-w-4xl tw-mx-auto tw-px-6 tw-text-center">
            <h2 className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-5 tw--tracking-tight">
              Ready to Simplify Your License Renewals?
            </h2>
            <p className="tw-text-lg tw-leading-relaxed tw-text-slate-300 tw-mb-10">
              Join hundreds of licensed professionals who trust CEC Track to manage their continuing education
            </p>
            <div className="tw-flex tw-gap-4 tw-justify-center tw-flex-wrap">
              <Link
                to={user ? "/dashboard" : "/register"}
                className="tw-inline-flex tw-items-center tw-px-8 tw-py-4 tw-bg-primary-500 tw-text-white tw-text-base tw-font-semibold tw-rounded-lg tw-no-underline tw-transition-all tw-duration-200 hover:tw-bg-primary-600 hover:tw--translate-y-0.5"
              >
                {user ? "Go to Dashboard" : "Get Started Free"}
                <svg className="tw-ml-2 tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/how-it-works"
                className="tw-inline-flex tw-items-center tw-px-8 tw-py-4 tw-bg-transparent tw-text-white tw-text-base tw-font-semibold tw-rounded-lg tw-no-underline tw-border-2 tw-border-slate-700 tw-transition-all tw-duration-200 hover:tw-border-primary-500 hover:tw-bg-slate-800"
              >
                Learn More
              </Link>
            </div>
            <div className="tw-mt-8">
              <p className="tw-text-sm tw-text-slate-400">
                Already have an account?{' '}
                <Link to="/login" className="tw-text-blue-400 tw-no-underline tw-font-medium hover:tw-text-blue-300">
                  Sign In →
                </Link>
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Landing;
